import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { containsTemplateVariable } from "@/components/ui/workflow-step-row/components/VariablePill";
import { useErc4626Decimals } from "@/hooks/useErc4626Decimals";
import type { TokenInfo } from "./ActionNodeValueFormat";
interface ActionTokenFormattingInput {
  /** Resolved underlying token info (from vault -> asset() -> SimDune lookup) */
  tokenInfo: TokenInfo | null;
  /** Vault contract address (from payload.vaultAddress) */
  vaultAddress?: string;
  /** Chain ID for RPC calls */
  chainId?: number;
  /** The action's payload (to read withdrawType) */
  payload?: Record<string, unknown>;
  /** Whether this action uses ERC-4626 vault resolution */
  hasErc4626Annotation: boolean;
}
interface ActionTokenFormattingResult {
  /** Decimals to use when formatting the input `amount` field on the canvas */
  inputAmountDecimals: number | undefined;
  /** Decimals for result fields in underlying asset units */
  resultDecimals: number | undefined;
  /** Decimals for result fields in vault share units (x-token-decimals: "vault") */
  vaultShareDecimals: number | undefined;
}

/**
 * Resolves the correct token decimals for formatting action node values.
 *
 * For ERC-4626 vault actions (Morpho deposit/withdraw):
 * - Input amount with withdrawType="shares" → uses vault's own decimals()
 * - Input amount with withdrawType="assets" (or default) → uses underlying token decimals
 * - Result fields → underlying decimals by default, vault decimals for fields with x-token-decimals: "vault"
 *
 * For non-vault actions, falls through to tokenInfo.decimals for both.
 */
export function useActionTokenFormatting(t0) {
  const $ = _c(4);
  const {
    tokenInfo,
    vaultAddress,
    chainId,
    payload,
    hasErc4626Annotation
  } = t0;
  const isSharesMode = payload?.withdrawType === "shares";
  const isResolvable = hasErc4626Annotation && !containsTemplateVariable(vaultAddress);
  const {
    data: vaultDecimals
  } = useErc4626Decimals(vaultAddress, chainId, isResolvable);
  tokenInfo?.decimals;
  let t1;
  const underlyingDecimals = tokenInfo?.decimals;
  const t2 = isSharesMode && vaultDecimals != null ? vaultDecimals : underlyingDecimals;
  let t3;
  if ($[0] !== t2 || $[1] !== underlyingDecimals || $[2] !== vaultDecimals) {
    t3 = {
      inputAmountDecimals: t2,
      resultDecimals: underlyingDecimals,
      vaultShareDecimals: vaultDecimals
    };
    $[0] = t2;
    $[1] = underlyingDecimals;
    $[2] = vaultDecimals;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  t1 = t3;
  return t1;
}